import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css, keyframes } from "@emotion/react";
import { addAlphaToHex } from "src/lib/emotion/utils.ts";
import type { HTMLAttributes, PropsWithChildren } from "react";

const horizontalShaking = keyframes`
    0% { transform: translateX(0) }
    25% { transform: translateX(2px) }
    50% { transform: translateX(-2px) }
    75% { transform: translateX(2px) }
    100% { transform: translateX(0) }
`;

const pulse = ({ theme, intense }: { theme: ExactTheme; intense: boolean }) =>
  keyframes`
    0% {
        box-shadow: 0 0 0 0 ${addAlphaToHex(
          theme.colorsV2.orange[intense ? 100 : 50],
          80,
        )};
    }
    100% {
        box-shadow: 0 0 0 20px ${addAlphaToHex(
          theme.colorsV2.orange[intense ? 100 : 50],
          0,
        )};
    }
`;

const wave1 =
  ({ intense }: { intense: boolean }) =>
  (theme: ExactTheme) => css`
    position: absolute;
    inset: 0;
    animation: ${pulse({ theme, intense })} 2s infinite;
    border-radius: inherit;
  `;
const wave2 =
  ({ intense }: { intense: boolean }) =>
  (theme: ExactTheme) => css`
    position: absolute;
    inset: 0;
    opacity: ${intense ? 1 : 0};
    animation: ${pulse({ theme, intense })} 2s infinite;
    animation-delay: 0.5s;
    border-radius: inherit;
    transition: opacity ease-in 0.3s;
  `;

const container = ({ shake }: { shake: boolean }) => [
  css`
    position: relative;
  `,
  shake &&
    css`
      animation: ${horizontalShaking} 0.25s infinite;
    `,
];

const inner = css`
  z-index: 1;
  position: relative;
`;

export const Pulsate = ({
  children,
  intense = false,
  shake = false,
  ...restProps
}: PropsWithChildren<
  { intense?: boolean; shake?: boolean } & HTMLAttributes<HTMLDivElement>
>) => {
  return (
    <div css={container({ shake })} {...restProps}>
      <div css={inner}>{children}</div>

      <span css={wave1({ intense })} />
      <span css={wave2({ intense })} />
    </div>
  );
};
